import { useCallback, useEffect, useRef, useMemo, useState } from "react";
import { BrandDetails } from "../types/add-brand-types";
import { useNavigation } from "./useNavigation";
import { AppRoute } from "../types/routes";
import { useCurrentUser } from "./useCurrentUser";
import { useTimeUtil } from "./useTimeUtil";
import { isNotEmpty } from "../utils/type-utils";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store/store";
import {
  fetchBrandList,
  fetchContentLogs,
  logoutAccount,
  clearDashboard,
} from "../store/features/dashboard/dashboardSlice";
import { logout as authLogout } from "../store/features/auth/authSlice";
import useContentStatsApi, { ContentStats } from "../api/useContentStatsApi";
import { ContentLog } from "../types/dashboard";
import linkedinIcon from "../assets/images/social/linkedin.svg";
import xIcon from "../assets/images/social/x-twitter.svg";
import instagramIcon from "../assets/images/social/instagram.svg";
import facebookIcon from "../assets/images/social/facebook.svg";
import threadsIcon from "../assets/images/social/threads.svg";
import redditIcon from "../assets/images/social/reddit.svg";

const DEFAULT_CONTENT_STATS: ContentStats = {
  scheduled: 0,
  draft: 0,
  published: 0,
  processing: 0,
  failed: 0,
  partial_failure: 0,
};

const normalizePlatformId = (platform: string): string => {
  const normalized = platform.toLowerCase();
  if (normalized === "linkedin") return "linkedin";
  if (normalized === "x") return "x";
  return normalized;
};

const resolvePlatform = (platform: unknown): string => {
  if (typeof platform === "string") return platform;
  if (
    platform &&
    typeof platform === "object" &&
    "platform" in platform &&
    typeof (platform as { platform?: unknown }).platform === "string"
  ) {
    return (platform as { platform: string }).platform;
  }
  return "";
};

const dedupeMedia = (media: { type: string; url: string }[] = []) => {
  const seen = new Set<string>();
  return media.filter((item) => {
    const key = `${item.type}:${item.url}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getCombinedStatus = (items: ContentLog[]): string => {
  const statuses = Array.from(
    new Set(items.map((item) => (item.status || "").toUpperCase()).filter(Boolean))
  );

  if (statuses.length === 0) return "";
  if (statuses.length === 1) return statuses[0];
  if (statuses.includes("PARTIAL_FAILURE")) return "PARTIAL_FAILURE";
  if (statuses.includes("FAILED") && statuses.some((status) => status !== "FAILED")) {
    return "PARTIAL_FAILURE";
  }
  if (statuses.includes("PROCESSING")) return "PROCESSING";
  if (statuses.includes("SCHEDULED") && statuses.includes("PUBLISHED")) {
    return "PROCESSING";
  }
  if (statuses.every((status) => status === "SCHEDULED")) return "SCHEDULED";
  return statuses[0];
};

const getCombinedFailureReason = (items: ContentLog[]): string | null => {
  const reasons = Array.from(
    new Set(
      items
        .map((item) => item.content?.pfm_result?.failure_reason?.trim())
        .filter((reason): reason is string => Boolean(reason))
    )
  );

  if (reasons.length === 0) return null;
  return reasons.join(" | ");
};

const useDashboard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { brandList, loadingBrandList, loadingContentLogs, contentLogs } =
    useSelector((state: RootState) => state.dashboard);

  const { push, replace } = useNavigation();
  const { user, loading: loadingUser } = useCurrentUser();
  const { formatDate } = useTimeUtil();
  const { getContentStats } = useContentStatsApi();
  const isLoggedOut = useRef<boolean>(false);

  const [contentStats, setContentStats] = useState<ContentStats>(DEFAULT_CONTENT_STATS);
  const [loadingStats, setLoadingStats] = useState(false);

  // Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    brand: BrandDetails | null;
  }>({
    isOpen: false,
    brand: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      // Only fetch if data is not available to persist state
      if (!brandList) {
        dispatch(fetchBrandList());
      }
      if (!contentLogs) {
        dispatch(fetchContentLogs());
      }
    };
    if (isNotEmpty(user?.id) && !isLoggedOut.current) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user?.id]); // Intentionally removed brandList/contentLogs to avoid loops if they change

  useEffect(() => {
    if (!isNotEmpty(user?.id) || isLoggedOut.current) return;
    let cancelled = false;
    setLoadingStats(true);
    getContentStats()
      .then((stats) => {
        if (!cancelled) setContentStats(stats);
      })
      .finally(() => {
        if (!cancelled) setLoadingStats(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, getContentStats]);

  const refetchStats = useCallback(() => {
    if (!isNotEmpty(user?.id)) return;
    setLoadingStats(true);
    getContentStats()
      .then(setContentStats)
      .finally(() => setLoadingStats(false));
  }, [user?.id, getContentStats]);

  const onAddBrandClick = useCallback(() => {
    push(AppRoute.ADD_BRAND);
  }, [push]);

  const onViewDetailsClick = useCallback(
    (brand: BrandDetails) => {
      const route = AppRoute.BRAND_DETAILS.replace(":id", brand.id);
      push(route, { brand });
    },
    [push]
  );

  const handleLogout = useCallback(async () => {
    // Call API to logout
    await dispatch(logoutAccount());
    
    // Clear Redux state
    dispatch(clearDashboard());
    dispatch(authLogout());
    
    isLoggedOut.current = true;

    // Clear local storage (authLogout already does this in reducer, but safe to do here too if needed, 
    // but reducer is better source of truth. The authSlice logout reducer clears access_token and account_details)
    // So we don't need manual localStorage.removeItem here if authSlice handles it.
  }, [dispatch]);

  const onDeleteClick = useCallback((brand: BrandDetails) => {
    setDeleteConfirmation({
      isOpen: true,
      brand,
    });
  }, []);

  const onDeleteCancel = useCallback(() => {
    setDeleteConfirmation({
      isOpen: false,
      brand: null,
    });
  }, []);

  const onDeleteConfirm = useCallback(async () => {
    const brandToDelete = deleteConfirmation.brand;
    if (!brandToDelete) return;

    // Close the modal
    setDeleteConfirmation({
      isOpen: false,
      brand: null,
    });

    // TODO: Call the API to delete the brand
    // Example: await dispatch(deleteBrand(brandToDelete.id));
    console.log("Deleting brand:", brandToDelete.id);
    
    // After successful deletion, refresh the brand list
    // dispatch(fetchBrandList());
  }, [deleteConfirmation.brand]);

  const getLogPlatforms = useCallback((log: ContentLog): string[] => {
    const rawPlatforms = log.content?.platforms ?? [];
    const resolvedPlatforms = rawPlatforms
      .map(resolvePlatform)
      .filter(Boolean)
      .map(normalizePlatformId);

    if (resolvedPlatforms.length > 0) {
      return Array.from(new Set(resolvedPlatforms));
    }

    return log.platform ? [normalizePlatformId(log.platform)] : [];
  }, []);

  const getIconForPlatform = useCallback((platform: string): string | null => {
    switch (platform) {
      case "linkedIn":
      case "linkedin":
        return linkedinIcon;
      case "twitter":
      case "x":
        return xIcon;
      case "instagram":
        return instagramIcon;
      case "facebook":
        return facebookIcon;
      case "threads":
        return threadsIcon;
      case "reddit":
        return redditIcon;
      default:
        return null;
    }
  }, []);

  const groupedContentLogs = useMemo(() => {
    if (!contentLogs) return contentLogs;

    const groupedLogs = new Map<string, ContentLog[]>();

    contentLogs.forEach((log) => {
      const key = log.post_id || log.id;
      const items = groupedLogs.get(key) ?? [];
      items.push(log);
      groupedLogs.set(key, items);
    });

    return Array.from(groupedLogs.values()).map((items) => {
      if (items.length === 1) {
        const [singleItem] = items;
        const failureReason = singleItem.content?.pfm_result?.failure_reason ?? null;
        return {
          ...singleItem,
          content: {
            ...singleItem.content,
            platforms: getLogPlatforms(singleItem),
            pfm_result: singleItem.content?.pfm_result
              ? {
                  ...singleItem.content.pfm_result,
                  overall_status:
                    singleItem.content.pfm_result.overall_status ?? singleItem.status,
                  failure_reason: failureReason,
                }
              : undefined,
          },
        };
      }

      const baseItem = items[0];
      const combinedStatus = getCombinedStatus(items);
      const combinedFailureReason = getCombinedFailureReason(items);
      const platforms = Array.from(
        new Set(items.flatMap((item) => getLogPlatforms(item)))
      );

      const platformCaptions = items.reduce<Record<string, string>>(
        (acc, item) => {
          const itemPlatforms = getLogPlatforms(item);
          const existingCaptions = item.content?.platformCaptions ?? {};

          itemPlatforms.forEach((platform) => {
            const caption =
              existingCaptions[platform] ??
              existingCaptions[normalizePlatformId(platform)] ??
              item.content?.content ??
              "";

            if (caption) {
              acc[platform] = caption;
            }
          });

          return acc;
        },
        {}
      );

      const platformMedia = items.reduce<
        Record<string, { type: string; url: string }[]>
      >((acc, item) => {
        const itemPlatforms = getLogPlatforms(item);
        const existingPlatformMedia = item.content?.platformMedia ?? {};

        itemPlatforms.forEach((platform) => {
          const mediaForPlatform = dedupeMedia(
            existingPlatformMedia[platform] ?? item.content?.media ?? []
          );
          if (mediaForPlatform.length > 0) {
            acc[platform] = mediaForPlatform;
          }
        });

        return acc;
      }, {});

      const mediaSignatures = Object.values(platformMedia).map((media) =>
        media.map((item) => `${item.type}:${item.url}`).join("|")
      );
      const uniqueMediaSignatures = new Set(
        mediaSignatures.filter((signature) => signature.length > 0)
      );
      const useCommonMediaForAllPlatforms = uniqueMediaSignatures.size <= 1;
      const commonMedia = useCommonMediaForAllPlatforms
        ? dedupeMedia(
            Object.values(platformMedia)[0] ?? baseItem.content?.media ?? []
          )
        : dedupeMedia(baseItem.content?.media ?? []);

      return {
        ...baseItem,
        status: combinedStatus,
        content: {
          ...baseItem.content,
          content:
            platformCaptions[platforms[0]] ??
            baseItem.content?.content ??
            "",
          platforms,
          platformCaptions,
          media: commonMedia,
          platformMedia:
            useCommonMediaForAllPlatforms || Object.keys(platformMedia).length === 0
              ? undefined
              : platformMedia,
          useCommonMediaForAllPlatforms,
          pfm_result:
            baseItem.content?.pfm_result || combinedFailureReason
              ? {
                  ...(baseItem.content?.pfm_result ?? {}),
                  status: combinedStatus,
                  overall_status: combinedStatus,
                  failure_reason: combinedFailureReason,
                }
              : undefined,
        },
      };
    });
  }, [contentLogs, getLogPlatforms]);

  return useMemo(
    () => ({
      brandList,
      loadingBrandList,
      loadingContentLogs,
      contentStats,
      loadingStats,
      refetchStats,
      onAddBrandClick,
      onViewDetailsClick,
      onDeleteClick,
      onDeleteCancel,
      onDeleteConfirm,
      deleteConfirmation,
      user,
      loadingUser,
      contentLogs: groupedContentLogs,
      rawContentLogs: contentLogs,
      getIconForPlatform,
      replace,
      push,
      handleLogout,
      formatDate,
    }),
    [
      brandList,
      loadingBrandList,
      loadingContentLogs,
      contentStats,
      loadingStats,
      refetchStats,
      onAddBrandClick,
      onViewDetailsClick,
      onDeleteClick,
      onDeleteCancel,
      onDeleteConfirm,
      deleteConfirmation,
      user,
      loadingUser,
      groupedContentLogs,
      contentLogs,
      getIconForPlatform,
      replace,
      push,
      handleLogout,
      formatDate,
    ]
  );
};

export default useDashboard;
